import {
  PAYMENT_METHOD_LABELS,
  formatMoney,
  plural,
  type MonthlyReport,
  type MonthlyTotals,
} from '@kominiarz/shared';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router';
import { Alert } from '../../components/Alert';
import { ButtonAnchor } from '../../components/Button';
import { ColorDot } from '../../components/ColorDot';
import { Spinner } from '../../components/Loading';
import { PageBody } from '../../components/PageBody';
import { PageHeader } from '../../components/PageHeader';
import { Section } from '../../components/Section';
import { errorMessage } from '../../lib/api';
import { capitalize, todayLocal } from '../../lib/calendarDates';
import { useMonthlyReport } from '../../lib/closingApi';
import styles from './summary.module.css';

const isMonth = (v: string | null): v is string => !!v && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);

/** Manager's monthly figures: what was done, what came in, what is still owed. */
export function SummaryPage() {
  const [params, setParams] = useSearchParams();
  const current = todayLocal().slice(0, 7);
  const requested = params.get('miesiac');
  const month = isMonth(requested) ? requested : current;
  const report = useMonthlyReport(month);
  const date = DateTime.fromISO(`${month}-01`).setLocale('pl');

  const go = (delta: number) => {
    const next = date.plus({ months: delta }).toFormat('yyyy-MM');
    setParams(next === current ? {} : { miesiac: next }, { replace: true });
  };

  return (
    <>
      <PageHeader title="Podsumowanie miesiąca" subtitle="Według daty wizyty" />
      <PageBody>
        <nav className={styles.monthNav} aria-label="Wybór miesiąca">
          <button type="button" className={styles.monthStep} onClick={() => go(-1)} aria-label="Poprzedni miesiąc">
            <ChevronLeft aria-hidden="true" />
          </button>
          <h2 className={styles.monthName} aria-live="polite">
            {capitalize(date.toFormat('LLLL yyyy'))}
          </h2>
          <button
            type="button"
            className={styles.monthStep}
            onClick={() => go(1)}
            disabled={month >= current}
            aria-label="Następny miesiąc"
          >
            <ChevronRight aria-hidden="true" />
          </button>
        </nav>

        {report.isPending && <Spinner />}
        {report.isError && <Alert kind="error">{errorMessage(report.error)}</Alert>}
        {report.data && <Report report={report.data} isCurrent={month === current} />}
      </PageBody>
    </>
  );
}

function Report({ report, isCurrent }: { report: MonthlyReport; isCurrent: boolean }) {
  const t = report.totals;
  const csv = (typ: string) => `/api/reports/monthly.csv?month=${report.month}&typ=${typ}`;

  return (
    <>
      <section className={styles.hero} aria-labelledby="summary-value">
        <p className={styles.heroLabel} id="summary-value">
          Wartość wykonanych wizyt
        </p>
        <p className={styles.heroValue}>{formatMoney(t.doneGrosze)}</p>
        <p className={styles.heroNote}>
          {t.doneCount === 0
            ? 'W tym miesiącu nie ma jeszcze wykonanych wizyt.'
            : `${plural(t.doneCount, 'wykonana wizyta', 'wykonane wizyty', 'wykonanych wizyt')}`}
        </p>
        {t.doneGrosze > 0 && <PaidMeter totals={t} />}
      </section>

      {t.doneWithoutAmount > 0 && (
        <Alert kind="info">
          {plural(t.doneWithoutAmount, 'wykonana wizyta nie ma', 'wykonane wizyty nie mają', 'wykonanych wizyt nie ma')}{' '}
          wpisanej kwoty, więc nie są liczone w sumach.
        </Alert>
      )}

      <dl className={styles.tiles}>
        <Tile label="Wykonane" value={String(t.doneCount)} />
        <Tile label={isCurrent ? 'Jeszcze zaplanowane' : 'Niezamknięte'} value={String(t.plannedCount)} />
        <Tile label="Odwołane" value={String(t.cancelledCount)} />
      </dl>

      {report.byAssignee.length > 0 && (
        <Section title="Według osoby" description="Wizyta zespołowa liczy się u każdej osoby, więc sumy osób mogą być większe niż suma miesiąca.">
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Osoba</th>
                <th scope="col" className={styles.num}>
                  Wizyty
                </th>
                <th scope="col" className={styles.num}>
                  Wartość
                </th>
                <th scope="col" className={styles.num}>
                  Opłacone
                </th>
                <th scope="col" className={styles.num}>
                  Do zapłaty
                </th>
              </tr>
            </thead>
            <tbody>
              {report.byAssignee.map((row) => (
                <tr key={row.assignee.id}>
                  <th scope="row">
                    <span className={styles.person}>
                      <ColorDot color={row.assignee.color} /> {row.assignee.name}
                    </span>
                  </th>
                  <td className={styles.num} data-label="Wizyty">
                    {row.doneCount}
                  </td>
                  <td className={styles.num} data-label="Wartość">
                    {formatMoney(row.doneGrosze)}
                  </td>
                  <td className={styles.num} data-label="Opłacone">
                    {formatMoney(row.paidGrosze)}
                  </td>
                  <td className={styles.num} data-label="Do zapłaty">
                    {formatMoney(row.unpaidGrosze)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {report.byMethod.length > 0 && (
        <Section title="Opłacone według formy płatności">
          <table className={`${styles.table} ${styles.compact}`}>
            <thead>
              <tr>
                <th scope="col">Forma płatności</th>
                <th scope="col" className={styles.num}>
                  Wizyty
                </th>
                <th scope="col" className={styles.num}>
                  Kwota
                </th>
              </tr>
            </thead>
            <tbody>
              {report.byMethod.map((row) => (
                <tr key={row.method ?? 'none'}>
                  <th scope="row">{row.method ? PAYMENT_METHOD_LABELS[row.method] : 'Bez formy płatności'}</th>
                  <td className={styles.num}>{row.count}</td>
                  <td className={styles.num}>{formatMoney(row.grosze)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      <Section
        title="Eksport do arkusza"
        description="Plik CSV otworzysz w Excelu albo w Arkuszach Google. Kwoty są z przecinkiem, kolumny rozdziela średnik."
      >
        <div className={styles.exports}>
          <ButtonAnchor href={csv('wizyty')} download variant="secondary" icon={<Download aria-hidden="true" />}>
            Lista wizyt (CSV)
          </ButtonAnchor>
          <ButtonAnchor href={csv('podsumowanie')} download variant="secondary" icon={<Download aria-hidden="true" />}>
            Podsumowanie (CSV)
          </ButtonAnchor>
        </div>
      </Section>
    </>
  );
}

/** Paid vs still owed, as one proportional bar with both figures labelled. */
function PaidMeter({ totals: t }: { totals: MonthlyTotals }) {
  const paidShare = t.doneGrosze > 0 ? (t.paidGrosze / t.doneGrosze) * 100 : 0;
  return (
    <div className={styles.meterBlock}>
      <div
        className={styles.meter}
        role="img"
        aria-label={`Opłacone ${formatMoney(t.paidGrosze)}, do zapłaty ${formatMoney(t.unpaidGrosze)}`}
      >
        {t.paidGrosze > 0 && <span className={styles.meterPaid} style={{ flexGrow: paidShare }} />}
        {t.unpaidGrosze > 0 && <span className={styles.meterUnpaid} style={{ flexGrow: 100 - paidShare }} />}
      </div>
      <ul className={styles.legend}>
        <li>
          <span className={`${styles.swatch} ${styles.meterPaid}`} aria-hidden="true" />
          Opłacone <strong>{formatMoney(t.paidGrosze)}</strong>
        </li>
        <li>
          <span className={`${styles.swatch} ${styles.meterUnpaid}`} aria-hidden="true" />
          Do zapłaty <strong>{formatMoney(t.unpaidGrosze)}</strong>
        </li>
      </ul>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.tile}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
